<script>
import { getFullDurationString } from '@artup/utilities'
import { useClientStore, useRouterStore, FormMixin } from '@artup/client'
import { mapState, mapActions } from 'pinia'
import { LoginError } from '@artup/accounts'
import { useAccountsStore } from '../../../store'
import AccountsForm from '../../../components/accounts-form.vue'
import AccountsMixin from '../../../components/accounts-mixin'

export default {
  mixins: [
    FormMixin,
    AccountsMixin
  ],

  components: {
    'artup-accounts-view': AccountsForm
  },

  data () {
    return {
      // Entered account name
      name: '',
      // Entered password
      password: '',
      // Flag indicating whether password should be displayed in clear text
      revealPassword: false,
      // Flag indicating that we're resetting the password
      resetsPassword: false
    }
  },

  computed: {
    ...mapState(useClientStore, [
      'isBusy',
    ]),

    ...mapState(useRouterStore, [
      'currentRoute'
    ]),

    ...mapState(useAccountsStore, [
      'session',
      'isLoggedIn',
      'isLoggingIn',
      'loginError',
      'loginMessage'
    ]),

    // Indicates that UI should be locked due to the ongoing action
    isLocked () {
      return this.isLoggingIn || this.isBusy
    },

    // Page to return to after succesful login
    goto () {
      return this.$route.query.goto
    },

    // Returns true if login failed due to locked account
    accountIsLocked () {
      return this.loginError === LoginError.Disabled
    },

    // Returns true if login failed due to too many failed login attempts
    tooManyFailedLogins () {
      return this.loginError === LoginError.TooManyFailedLogins
    },

    // Information when is the next login allowed,
    // after locking due to too many failed logins
    nextLoginAllowed () {
      return getFullDurationString(this.configuration.security.login.resetLoginAttemptsAfter)
    },

    // Indicates that all inputs are okay and the user can log in
    canLogIn () {
      return !this.isLocked && this.name.trim() !== '' && this.password.trim() !== ''
    },

    // Indicates that the user can reset the password
    canResetPassword () {
      return !this.isLocked && this.name.trim() !== ''
    }
  },

  methods: {
    ...mapActions(useClientStore, [
      'busy',
      'done'
    ]),

    ...mapActions(useAccountsStore, [
      'clearSession',
      'authenticate',
      'startSession',
      'resetPassword'
    ]),

    // Populates the dialog
    populate ({ name } = {}) {
      this.name = name || ''
    },

    // Attempts the login
    tryLogin () {
      this.secure(async reCaptchaToken => {
        const { name, password, goto } = this
        if (!await this.validate()) return
        const isAuthenticated = await this.authenticate({ name, password, reCaptchaToken })
        if (isAuthenticated) {
          const { session } = this
          await this.startSession({ session, goto })
        }
      })
    },

    // Restores the password
    async reset (reCaptchaToken) {
      const { name } = this
      if (name) {
        await this.busy({ message: `Requesting new password for ${name} ...` })
        await this.resetPassword({ name, sendEmail: true, reCaptchaToken })
        await this.done({ message: 'Password reset requested', details: 'Please check your mailbox for further instructions.' })
        this.resetsPassword = false
        this.password = ''
      }
    }
  },

  async beforeRouteUpdate (to) {
    await this.populate({ name: to.query.name })
  },

  async mounted () {
    await this.clearSession()
    await this.populate({ name: this.currentRoute.query.name })
  }
}

</script>

<template>
  <artup-accounts-view v-if="!isLoggedIn" v-cloak class="login" title="Log In"
    subtitle="Welcome to the ARTup Community">

    <template #content>
      <q-form ref="form" class="form q-mt-lg">
        <div class="fields">

          <div class="account">
            <q-input label="E-mail address" class="input-name" outlined v-model="name"
              :disable="isLocked" autocomplete="username" maxlength="45"
              @keydown.enter="tryLogin()">
            </q-input>
            <div class="row justify-end q-mt-sm">
              <a v-if="resetsPassword" @click.stop="resetsPassword = false" class="q-mr-md">
                Back to Login
              </a>
            </div>
          </div>

          <div class="password q-mt-md" v-if="!resetsPassword">
            <q-input label="Password" class="input-password" outlined :disable="isLocked"
              v-model="password" autocomplete="current-password" maxlength="45"
              :type="revealPassword ? 'text' : 'password'" @keydown.enter="tryLogin()">
              <template v-slot:append>
                <q-icon name="visibility" class="button-reveal-password cursor-pointer"
                  :disable="isLocked" @click="revealPassword = !revealPassword">
                </q-icon>
              </template>
            </q-input>
            <div class="text-center q-mt-sm">
              <a @click.stop="resetsPassword = true">
                Forgot password?
              </a>
            </div>
          </div>
        </div>

        <div v-if="resetsPassword" class="buttons reset q-mt-lg">
          <label class="text-center q-mb-md">
            Click the button below to receive an e-mail message with password reset link.
          </label>
          <q-btn class="button-reset bg-grey-9 text-white q-pb-md q-pt-md" label="Reset Password"
            unelevated rounded no-caps @click="reset()" :disable="!canResetPassword">
          </q-btn>
        </div>
        <div v-else class="buttons login q-mt-lg q-gutter-md">
          <q-btn class="button-login bg-grey-8 text-white q-pb-md q-pt-md" label="Log In" unelevated
            rounded no-caps @click="tryLogin()" :disable="!canLogIn">
          </q-btn>
          <q-btn class="buttons signup text-white q-pb-md q-pt-md" label="Create Account" unelevated
            rounded no-caps :to="{ name: 'signup', query: { name } }">
          </q-btn>
        </div>
      </q-form>
    </template>

  </artup-accounts-view>
</template>

<style scoped lang="scss">
.accounts-view {
  form {
    .buttons.reset {
      height: 140px;
    }

    .buttons.signup {
      background-color: #c32b75;
    }
  }
}
</style>